import clsx from 'clsx';
import React, { ReactNode, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleModal } from '../../reducers/modalToggle';

interface ModalComponentProps {
  otherClasses?: string;
  additionalClass?: string;
  children: ReactNode;
  customClose?: () => void;
}

const ModalComponent: React.FC<ModalComponentProps> = ({
  otherClasses,
  children,
  additionalClass,
  customClose,
}) => {
  const dispatch = useDispatch();
  const { modalName } = useSelector(
    ({ modalToggle }: { modalToggle: { modalName: boolean } }) => modalToggle,
  );

  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(
    null,
  );
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setModalContainer(document.body);
    }
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');

    if (modalName && root) {
      // Remove root from tab order and hide from assistive tech
      root.setAttribute('tabIndex', '-1');
      root.setAttribute('aria-hidden', 'true');

      // Move focus inside modal
      setTimeout(() => {
        modalRef.current?.focus();
      }, 0);
    }

    return () => {
      if (root && !modalName) {
        root.removeAttribute('tabIndex');
        root.removeAttribute('aria-hidden');
      }
    };
  }, [modalName]);

  const modalComponentClasses = clsx(
    otherClasses,
    'flex justify-center fixed top-0 left-0 bottom-0 right-0 transition duration-300 bg-[#00000080] overflow-auto',
    modalName ? 'opacity-100 z-50 visible' : 'opacity-0 invisible',
  );

  if (!modalName || !modalContainer) return null;

  return createPortal(
    <div
      ref={modalRef}
      tabIndex={-1}
      className={modalComponentClasses}
      data-testid="modal-component"
      role="dialog"
      aria-modal="true"
    >
      <button
        aria-label="button-close"
        title="button-close"
        aria-labelledby="button-close"
        tabIndex={-1}
        className="absolute top-0 right-0 bottom-0 left-0 z-[-1] h-full w-full cursor-auto backdrop-blur-sm"
        onClick={() =>
          customClose ? customClose() : dispatch(toggleModal(''))
        }
      />
      <div
        className={clsx(
          'bg-translucent-background my-auto h-auto overflow-auto',
          additionalClass,
        )}
      >
        {children}
      </div>
    </div>,
    modalContainer,
  );
};

export default ModalComponent;
